import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api as apiConfig } from "../../config";

/**
 * Hook del portal público de Inspección de Trabajo.
 *
 * Maneja el token (sessionStorage), expone una instancia axios sin
 * interceptors que añade el header X-Inspection-Token a cada petición, y
 * valida automáticamente al montar llamando a /workspace.
 *
 * Si no hay token o el servidor responde 401, redirige a /inspeccion.
 */

const STORAGE_KEY = "kronaly_inspection_token";

export function getStoredToken() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch (_) {
    /* ignore */
  }
}

export function clearStoredToken() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

/**
 * Devuelve una instancia axios sin interceptors (NO añade JWT, NO añade
 * X-Workspace-Id). Auth exclusivamente vía X-Inspection-Token.
 */
export function makeInspectionAxios(token) {
  return axios.create({
    baseURL: apiConfig.API_URL,
    withCredentials: false,
    headers: token ? { "X-Inspection-Token": token } : {},
  });
}

/**
 * Hook completo para pantallas internas del portal: precarga workspace,
 * gestiona loading/error, ofrece http y logout.
 */
export default function useInspectionPortal({ redirectIfNoToken = true } = {}) {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => getStoredToken());
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const http = useMemo(() => makeInspectionAxios(token), [token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      if (redirectIfNoToken) navigate("/inspeccion", { replace: true });
      return;
    }
    let mounted = true;
    setLoading(true);
    http
      .get("/inspection/workspace")
      .then((r) => {
        if (!mounted) return;
        setWorkspace(r?.data?.data || r?.data || null);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        const status = err?.response?.status;
        if (status === 401) {
          clearStoredToken();
          setToken(null);
          if (redirectIfNoToken) navigate("/inspeccion", { replace: true });
          return;
        }
        setError(err?.response?.data?.error?.message || err?.message || "Error al cargar workspace.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [token, http, navigate, redirectIfNoToken]);

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    navigate("/inspeccion", { replace: true });
  }, [navigate]);

  return { token, workspace, http, loading, error, logout };
}
